import React, { useContext, useEffect, useState, useRef } from 'react';
import KrwPrice from '../../components/KrwPrice';
import EntryTime from '../../components/EntryTime';
import BankInfo from '../../components/BankInfo';
import { Radio, Space, Tag, Spin } from 'antd';
import TableAPI from '../../components/TableAPI';
import { useTradeModalStore } from '../../hooks/trade/modalStore';
import { COLORS } from '../../constants';
import { ThemeContext } from '@/theme/ThemeProvider.tsx';
import { formatDecimal4Truncate } from '@/utils/formatDecimal';
import { t } from 'i18next';
import styled, { keyframes } from 'styled-components';
import { media } from '@/styles/Qtheme.ts';
import EmptyMessage from '@/app/components/EmptyMessage';
import { LoadingOutlined } from '@ant-design/icons';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
`;

const LoadingOverlay = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.itemBg}e6;
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: ${({ isVisible }) => (isVisible ? 'all' : 'none')};
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 18px;
`;

const StyledTableWrapper = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.itemBg};
  border-radius: 18px;
  border: 1px solid
    ${({ theme }) => theme.COLORS.border || 'rgba(255, 255, 255, 0.1)'};
  box-shadow:
    0 6px 24px rgba(0, 0, 0, 0.11),
    0 2px 6px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(15px);
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  animation: ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(0, 122, 255, 0.04) 0%,
      rgba(255, 76, 0, 0.02) 40%,
      transparent 70%
    );
    transform: translate(-50%, -50%);
    pointer-events: none;
    animation: ${floatAnimation} 6s ease-in-out infinite;
  }

  &:hover {
    transform: translateY(-3px) scale(1.005);
    box-shadow:
      0 12px 40px rgba(0, 122, 255, 0.2),
      0 6px 12px rgba(255, 76, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.18),
      0 0 0 1px rgba(0, 122, 255, 0.12),
      0 0 15px rgba(255, 76, 0, 0.08);
  }

  @media ${media.mobile} {
    border-radius: 14px;

    &:hover {
      transform: translateY(-2px) scale(1.002);
    }
  }

  .ant-card {
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;

    .ant-card-head {
      background: linear-gradient(
        135deg,
        ${({ theme }) => theme.itemBg || '#ffffff'} 0%,
        ${({ theme }) => theme.COLORS.primary || '#007bff'}08 100%
      );
      border-bottom: 1px solid
        ${({ theme }) => theme.COLORS.border || 'rgba(255, 255, 255, 0.1)'};
      padding: 20px 24px;

      .ant-card-head-title {
        font-weight: 600;
        font-size: 18px;
      }
    }

    .ant-card-body {
      padding: 0;
      background: transparent;
    }
  }

  .ant-table-wrapper {
    .ant-table {
      background: transparent !important;

      .ant-table-thead > tr > th {
        background: linear-gradient(
          135deg,
          ${({ theme }) => theme.itemBg || '#ffffff'} 0%,
          ${({ theme }) => theme.COLORS.primary || '#007bff'}05 100%
        );
        border-bottom: 1px solid
          ${({ theme }) => theme.COLORS.border || 'rgba(255, 255, 255, 0.1)'};
        color: ${({ theme }) => theme.COLORS.textw || '#ffffff'};
        font-weight: 600;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 16px 12px;
      }

      .ant-table-tbody > tr {
        background: transparent !important;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        border-bottom: 1px solid
          ${({ theme }) => theme.COLORS.border || 'rgba(255, 255, 255, 0.05)'};

        &:hover {
          background: linear-gradient(
            135deg,
            ${({ theme }) => theme.itemBg || '#ffffff'} 0%,
            ${({ theme }) => theme.COLORS.primary || '#007AFF'}05 100%
          ) !important;
        }

        > td {
          border-bottom: 1px solid
            ${({ theme }) => theme.COLORS.border || 'rgba(255, 255, 255, 0.05)'};
          padding: 16px 12px;
          font-size: 14px;
          font-weight: 500;
        }
      }

      .ant-table-tbody > tr:last-child > td {
        border-bottom: none;
      }
    }
  }

  .ant-tag {
    border-radius: 12px;
    font-weight: 600;
    padding: 4px 12px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    /* 승인 상태 (기본) */
    &:not([style*='#']) {
      background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
      color: white;
      box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
    }

    /* 주황색 태그 (출금요청 상태) */
    &[style*='#f79336'] {
      background: linear-gradient(135deg, #f79336 0%, #ea580c 100%) !important;
      color: white !important;
      box-shadow: 0 2px 8px rgba(247, 147, 54, 0.3);
    }
  }
`;

const DWList = () => {
  const { data: modalStore, mutate: mutateModalStore } = useTradeModalStore();
  const { textw } = useContext(ThemeContext).theme.COLORS;
  const [refresh, setRefresh] = React.useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const prevDepositTypeRef = useRef(modalStore?.depositType);

  useEffect(() => {
    if (!modalStore?.depositType || modalStore?.depositType === '*') {
      mutateModalStore({
        ...modalStore,
        depositType: 'D',
      });
      setRefresh((prev) => !prev);
    }
  }, [modalStore?.depositType, mutateModalStore]);

  // 탭 전환 시 로딩 애니메이션 처리
  useEffect(() => {
    if (prevDepositTypeRef.current && prevDepositTypeRef.current !== modalStore?.depositType) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 400);
      prevDepositTypeRef.current = modalStore?.depositType;
      return () => clearTimeout(timer);
    }
    prevDepositTypeRef.current = modalStore?.depositType;
  }, [modalStore?.depositType]);

  const getTabPosition = (value) => {
    switch (value) {
      case 'D':
        return '0%';
      case 'W':
        return '100%';
      case 'WR':
        return '200%';
      default:
        return '0%';
    }
  };
  return (
    <StyledTableWrapper
      style={{
        ['--tab-position' as any]: getTabPosition(
          modalStore?.depositType || 'D'
        ),
      }}
    >
      <LoadingOverlay isVisible={isLoading}>
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
          size="large"
        />
      </LoadingOverlay>
      <TableAPI
        title={
          <Space.Compact style={{ padding: '10px 0' }}>
            <Radio.Group
              value={modalStore?.depositType || 'D'}
              buttonStyle="solid"
              onChange={(e) => {
                mutateModalStore({
                  ...modalStore,
                  depositType: e.target.value,
                });
              }}
            >
              {/*<Radio.Button value="*">All</Radio.Button>*/}
              <Radio.Button value="D">{t('deposit')}</Radio.Button>
              <Radio.Button value="W">{t('withdraw')}</Radio.Button>
              <Radio.Button value="WR">{t('withdrawList')}</Radio.Button>
            </Radio.Group>
          </Space.Compact>
        }
        defaultMaxData={50}
        stylewidth={'100%'}
        columns={() => {
          if (modalStore?.depositType === 'D') {
            return [
              {
                title: t('date'),
                dataIndex: 'sentAt',
                align: 'left',
                render: (text, record) => {
                  return (
                    <EntryTime
                      time={record?.sentAt}
                      style={{ color: textw, justifyContent: 'flex-start' }}
                      offset={9}
                    />
                  );
                },
              },
              {
                title: t('amount'),
                dataIndex: 'amount',
                align: 'left',
                render: (text, record) => {
                  return (
                    <KrwPrice
                      price={formatDecimal4Truncate(
                        record?.type == 'W' ? -Math.abs(text) : Math.abs(text)
                      )}
                      krwprice={formatDecimal4Truncate(record?.krwprice)}
                      dp={4}
                      fontSize="14px"
                    />
                  );
                },
              },
              {
                title: t('id'),
                dataIndex: 'id',
                align: 'left',
                render: (text, record) => {
                  return <div style={{ color: textw }}>{text}</div>;
                },
              },
              {
                title: t('status'),
                dataIndex: 'process',
                align: 'left',
                render: (text, data) => <Tag>{t('approval')}</Tag>,
              },
              {
                title: t('approvalDate'),
                dataIndex: 'confirm_at',
                align: 'left',
                render: (text, record) => {
                  return (
                    <EntryTime
                      time={record?.sentAt}
                      style={{ color: textw, justifyContent: 'flex-start' }}
                      offset={9}
                    />
                  );
                },
              },
              {
                title: t('approvalAmount'),
                dataIndex: 'amount',
                align: 'left',
                render: (text, record) => {
                  return (
                    <KrwPrice
                      price={formatDecimal4Truncate(
                        record?.type == 'W' ? -Math.abs(text) : Math.abs(text)
                      )}
                      krwprice={formatDecimal4Truncate(record?.krwprice)}
                      dp={4}
                      fontSize="14px"
                    />
                  );
                },
              },
            ];
          } else if (modalStore?.depositType === 'W') {
            return [
              {
                title: t('date'),
                dataIndex: 'sentAt',
                align: 'left',
                render: (text, record) => {
                  return (
                    <EntryTime
                      time={record?.sentAt}
                      style={{ color: textw, justifyContent: 'flex-start' }}
                      offset={9}
                    />
                  );
                },
              },
              {
                title: t('amount'),
                dataIndex: 'amount',
                align: 'left',
                render: (text, record) => {
                  return (
                    <KrwPrice
                      price={formatDecimal4Truncate(
                        record?.type == 'W' ? -Math.abs(text) : Math.abs(text)
                      )}
                      krwprice={formatDecimal4Truncate(record?.krwprice)}
                      dp={4}
                      fontSize="14px"
                    />
                  );
                },
              },
              {
                title: t('id'),
                dataIndex: 'id',
                align: 'left',
                render: (text, record) => {
                  return <div style={{ color: textw }}>{text}</div>;
                },
              },
              {
                title: t('status'),
                dataIndex: 'process',
                align: 'left',
                render: (text, data) => <Tag>{t('approval')}</Tag>,
              },
              {
                title: t('approvalDate'),
                dataIndex: 'confirm_at',
                align: 'left',
                render: (text, record) => {
                  return (
                    <EntryTime
                      time={record?.sentAt}
                      style={{ color: textw, justifyContent: 'flex-start' }}
                      offset={9}
                    />
                  );
                },
              },
              {
                title: t('approvalAmount'),
                dataIndex: 'amount',
                align: 'left',
                render: (text, record) => {
                  return (
                    <KrwPrice
                      price={formatDecimal4Truncate(
                        record?.type == 'W' ? -Math.abs(text) : Math.abs(text)
                      )}
                      krwprice={formatDecimal4Truncate(record?.krwprice)}
                      dp={4}
                      fontSize="14px"
                    />
                  );
                },
              },
            ];
          } else {
            // WR - 출금요청 리스트
            return [
              {
                title: t('date'),
                dataIndex: 'requestedAt',
                align: 'left',
                render: (text, record) => {
                  return (
                    <EntryTime
                      time={record?.requestedAt}
                      style={{ color: textw, justifyContent: 'flex-start' }}
                      offset={9}
                    />
                  );
                },
              },
              {
                title: t('amount'),
                dataIndex: 'amount',
                align: 'left',
                render: (text, record) => {
                  return (
                    <KrwPrice
                      price={
                        record?.type == 'W' ? -Math.abs(text) : Math.abs(text)
                      }
                      krwprice={record?.krwprice}
                    />
                  );
                },
              },
              {
                title: t('fee'),
                dataIndex: 'fee',
                align: 'left',
                render: (text, record) => {
                  return (
                    <div style={{ color: textw }}>
                      {Number(text).toFixed(2)}
                    </div>
                  );
                },
              },
              {
                title: t('id'),
                dataIndex: 'id',
                align: 'left',
                render: (text, record) => {
                  return <div style={{ color: textw }}>{text}</div>;
                },
              },
              {
                title: t('walletAddress'),
                dataIndex: '',
                align: 'left',
                render: (text, record) => {
                  return (
                    <div style={{ color: textw }}>
                      {record?.address?.address}
                    </div>
                  );
                },
              },
              {
                title: t('status'),
                dataIndex: 'status',
                align: 'left',
                render: (text, data) => (
                  <div>
                    <Tag
                      style={{
                        backgroundColor: '#f79336',
                        color: 'white',
                      }}
                    >
                      {text}
                    </Tag>
                  </div>
                ),
              },
              {
                title: t('memo'),
                dataIndex: 'note',
                align: 'left',
                render: (text, record) => {
                  return <BankInfo text={text} theme={false} />;
                },
              },
            ];
          }
        }}
        apikey={
          modalStore?.depositType === 'D'
            ? '/cash/list?type=D&t=' + (refresh ? 1 : 0)
            : modalStore?.depositType === 'W'
              ? '/cash/list?type=W&t=' + (refresh ? 1 : 0)
              : '/withdraw/requests?page=1&maxData=10'
        }
        noData={<EmptyMessage />}
      />
    </StyledTableWrapper>
  );
};

export default DWList;
